import type { Router } from "vue-router";

import { isNativePlatform } from "../capacitor/platform";
import { readSystemStatus } from "../queries/system";

// Skipped on native, where a reload loads the same local bundle.

export const BUNDLE_CHECK_INTERVAL_MS = 60_000;
// While a fleet upgrades, the nodes behind the balancer answer with different
// hashes, and every answer that differs would reload the tab again.
export const BUNDLE_RELOAD_COOLDOWN_MS = 10 * 60_000;
const RELOADED_AT_KEY = "knBundleReloadAt";

let lastAskedAt = Number.NEGATIVE_INFINITY;
// What the server answered this tab's first check, which stands in for the
// bundle the tab is running. Per tab: another tab having seen a new hash says
// nothing about the bundle this one loaded.
let running: string | null = null;

// sessionStorage throws when site data is blocked; that tab is not held to the cooldown.
function reloadedWithinCooldown(now: number): boolean {
  try {
    return now - Number(sessionStorage.getItem(RELOADED_AT_KEY)) < BUNDLE_RELOAD_COOLDOWN_MS;
  } catch {
    return false;
  }
}

function markReloaded(now: number): void {
  try {
    sessionStorage.setItem(RELOADED_AT_KEY, String(now));
  } catch {
    // Nothing to do: see reloadedWithinCooldown.
  }
}

/** Reloads the page when `hash` is not the bundle this tab is running. */
function compareBundleHash(hash: string | undefined, now: number): void {
  if (!hash) return;
  if (running === null) {
    running = hash;
    return;
  }
  if (hash === running || reloadedWithinCooldown(now)) return;
  markReloaded(now);
  window.location.reload();
}

async function check(): Promise<void> {
  const now = Date.now();
  if (now - lastAskedAt < BUNDLE_CHECK_INTERVAL_MS) return;
  lastAskedAt = now;
  try {
    compareBundleHash((await readSystemStatus()).JsBundleHash, now);
  } catch {
    // The next check asks again.
  }
}

export function installBundleHashCheck(router: Router): void {
  if (isNativePlatform()) return;
  router.afterEach(() => void check());
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void check();
  });
}
